import { v4 as uuidv4 } from 'uuid';
import { ConnectorIdRepository } from '../domain/ConnectorIdRepository';
import { ConnectorId } from '../domain/ConnectorId';

export function getValue<T, K extends keyof T>(data: T, key: K) :any{
    let nData:any = data;

    if (typeof(key) == 'string') {
        const keys = key.split('.')
        keys.map((f:string) => {nData = nData && nData[f]})
        return nData;
    }

    return nData[key];
}

const wait_miliseconds=(milis:number): Promise<void> => {
    return new Promise((resolve) => {
        setTimeout(resolve, milis);
    });
}

export class MemoryFilter {
    field:any
    value:any
    operator:string

    constructor(field: string,value:string, operator:string) 
    {
        this.field = field
        this.value = value
        this.operator = operator
    }
    matches(item):boolean {
        if(this.operator=='=='){
            if(getValue(item,this.field)==this.value){
                return true
            }
        }
        return false
    }
}

export class MemoryCriteria{
    filters:any=[[]]
    filter_class:any

    aux(f_and:any){
        return new this.filter_class(f_and[0],f_and[1],f_and[2])
    }

    constructor(filters:any[][]) 
    {
        if(this.filter_class===undefined)
            this.filter_class=MemoryFilter
        const filt = filters.map(f_or=>{
            return f_or.map(f_and=>{
                return this.aux(f_and)
            })
        })
        this.filters = filt
    }
    matches(obj):boolean {
        if (this.filters.length === 0) {
            return true;
        }

        // Iterate over each group of filters (OR conditions)
        for (const group of this.filters) {
            let groupMatches = true;
            // Check if all filters in the group match (AND conditions)
            for (const filter of group) {
                if (!filter.matches(obj)) {
                    groupMatches = false;
                    break;
                }
            }
            // If any filter in the group matches, return true
            if (groupMatches) {
                return true;
            }
        }

        // If none of the groups match, return false
        return false;
    }
}

export default class MemoryItemsRepo {
    private data_dict: { [key: string]: any };
    private data_keys: string[];
    private errors: any[];
    wait_milis = 100;
    criteria_class:any
    clone_depth:number

    constructor(data: any[], clone_depth = 1, errors:number[]=[]) {
        if(this.criteria_class===undefined)
            this.criteria_class=MemoryCriteria
        this.clone_depth = clone_depth;
        this.data_dict = {};
        this.data_keys = [];
        this.errors = errors;
        const uniqueIds = new Set();
        for(let i = 0; i < data.length; i++){
            if(!uniqueIds.has(data[i].id)) {
                this.data_dict[data[i].id as unknown as string] = data[i];
                this.data_keys.push(data[i].id);
                uniqueIds.add(data[i].id);
            }
        }
    }

    async find_by_id(id): Promise<any | undefined>
    {
        await wait_miliseconds(this.wait_milis);
        return this.data_dict[id as unknown as string].clone(this.clone_depth);
    }

    async find_all(): Promise<any[]>
    {
        const res: any[] = [];
        await wait_miliseconds(this.wait_milis);
        for(let i = 0; i < this.data_keys.length; i++){
            res.push(this.data_dict[this.data_keys[i] as unknown as string].clone(this.clone_depth));
        }
        return res;
    }

    async find_by_criteria(crit:any[][]): Promise<any[]> {
        const res: any[] = [];
        const criteria = new this.criteria_class(crit)
        await wait_miliseconds(this.wait_milis);
        for(let i = 0; i < this.data_keys.length; i++){
            if (criteria.matches(this.data_dict[this.data_keys[i] as unknown as string])){
                res.push(this.data_dict[this.data_keys[i] as unknown as string].clone(this.clone_depth));
            }
        }
        return res;
    }

    async find_by_criteria_count(crit:any[][],limit:number|undefined, offset:number|undefined): Promise<[any[],number]> {
        const res: any[] = [];
        const number = 0;
        const criteria = new this.criteria_class(crit)
        await wait_miliseconds(this.wait_milis);
        for(let i = 0; i < this.data_keys.length; i++){
            if (criteria.matches(this.data_dict[this.data_keys[i] as unknown as string])){
                res.push(this.data_dict[this.data_keys[i] as unknown as string].clone(this.clone_depth));
            }
        }
        return [res,number];
    }


    async save(item): Promise<any> {
        if (!item.id) {
            if (typeof this.data_keys[0] == 'string') {
                item.id = uuidv4();
            } else {
                if (this.data_keys.length) {
                    let id: any = 0;
                    for (const i of this.data_keys) {
                        if (i > id) {
                            id = i
                        }
                    }
                    item.id = id + 1;
                } else {
                    item.id = 1;
                }
            }
        }
        await wait_miliseconds(this.wait_milis);
        this.data_dict[item.id] = item.clone(this.clone_depth);
        if (!this.data_keys.includes(item.id)) {
            this.data_keys.push(item.id);
        }
        return item.id
    }

    async borrar(id: any): Promise<void> {
        if (this.errors.includes(id)) {
            throw new Error('ERROR BORRANDO.');
        }
        if (this.data_keys.includes(id)) {
            const index = this.data_keys.indexOf(id);
            if (index > -1) {
                this.data_keys.splice(index, 1);
            }
            delete this.data_dict[id]
        }
    }
}

export class MemoryConnectorIdRepository implements ConnectorIdRepository {
    private data_dict: ConnectorId[];

    constructor(data: ConnectorId[]) {
        this.data_dict = data;
    }

    async getConnectors(user_id: Number): Promise<ConnectorId[]> {
        return this.data_dict.filter(c => c.user_id === user_id)
    }

    async setCurrentConnector(user_id: Number, connectorName: String): Promise<void> {
        this.data_dict.forEach(c => {
            if (c.user_id === user_id) {
                if (c.name === connectorName) {
                    c.current = true;
                } else {
                    c.current = false;
                }
            }
        })
    }

    find_by_id(user_id, name: string): ConnectorId | undefined {

        const matchingConnectors = this.data_dict.filter(
            connector => connector.name === name && connector.user_id === user_id
        );
        
        return matchingConnectors.length ? matchingConnectors[0] : undefined;
    }    
}